import React from 'react';
import { GatsbyImage, StaticImage, getImage } from 'gatsby-plugin-image';
import SectionLayout from '../layouts/SectionLayout/index';
import RawHTML from '@components/Common/RawHTML/index';
import { cn } from '@/lib/utils';

const TechStackSection = React.memo(function TechStackSection({
  title,
  subtitle,
  eyebrow,
  image,
  techStack,
  ...props
}) {
  const topImage = getImage(image?.childImageSharp);
  const stack = techStack?.length ? techStack : fallbackStack;

  return (
    <SectionLayout heading={''} description={''} removeDescription {...props}>
      <div className="container">
        <div className="mb-12 flex flex-col items-center text-center">
          {topImage && (
            <div className="mb-6 w-full max-w-[360px]">
              <GatsbyImage
                image={topImage}
                alt={title ? `${title}` : `Technologies You'll Master`}
                loading="lazy"
                imgStyle={{ objectFit: 'contain' }}
              />
            </div>
          )}
          {eyebrow && (
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-v5-green-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-v5-green-300">
              <span className="h-2 w-2 rounded-full bg-v5-green-300"></span>
              <RawHTML>{eyebrow}</RawHTML>
            </span>
          )}
          <h2 className="z-[300] mb-5 font-manrope font-extrabold capitalize leading-snug md:px-32">
            <RawHTML>{title || `Technologies You'll Master`}</RawHTML>
          </h2>
          {subtitle && (
            <p className="leading-loose text-v5-neutral-400 font-light sm:px-44">
              <RawHTML>{subtitle}</RawHTML>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {stack.map((card, idx) => (
            <div
              key={idx}
              className={cn(
                'flex flex-col rounded-[20px] border p-6 transition-all',
                card.featured
                  ? 'border-v5-green-300 bg-v5-green-100'
                  : 'border-v5-neutral-300 bg-white',
              )}
            >
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-[14px] bg-v5-neutral-200 p-2 text-2xl">
                  <TechIcon icon={card.icon} alt={card.title} />
                </div>
                <div className="text-left">
                  <h3 className="font-manrope text-lg font-bold leading-tight text-v5-neutral-600">
                    <RawHTML>{card.title}</RawHTML>
                  </h3>
                  {card.subtitle && (
                    <div className="mt-0.5 text-sm text-v5-neutral-400">
                      {card.subtitle}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {card.chips?.map((chip, i) => (
                  <span
                    key={i}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-xs font-semibold',
                      card.featured
                        ? 'bg-v5-green-200 text-white'
                        : 'bg-v5-neutral-200 text-v5-neutral-500',
                    )}
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionLayout>
  );
});

export default TechStackSection;

// Renders the card icon: a Gatsby image node from YAML, or a React element /
// string (used by the fallback below).
function TechIcon({ icon, alt }) {
  const image = getImage(icon?.childImageSharp);

  if (image) {
    return (
      <GatsbyImage
        image={image}
        alt={typeof alt === 'string' ? alt.replace(/<[^>]*>/g, '') : 'technology icon'}
        loading="lazy"
        imgStyle={{ objectFit: 'contain' }}
        className="h-full w-full"
      />
    );
  }

  return icon;
}

// Fallback used when the `techStack` prop is not supplied from YAML.
const fallbackStack = [
  {
    icon: (
      <StaticImage
        src="../../../images/v5/CurriculumIcons/DataScience/Icon1.webp"
        alt="Data Analytics & BI"
        loading="lazy"
        placeholder="blurred"
        className="h-full w-full"
        imgStyle={{ objectFit: 'contain' }}
      />
    ),
    title: 'Data Analytics & BI',
    subtitle: 'Query, transform, visualize',
    chips: [
      'SQL',
      'Python',
      'Pandas',
      'Tableau',
      'Power BI',
      'Excel',
      'Seaborn',
      'NumPy',
    ],
  },
  {
    icon: (
      <StaticImage
        src="../../../images/v5/CurriculumIcons/DataScience/Icon2.webp"
        alt="Machine Learning & MLOps"
        loading="lazy"
        placeholder="blurred"
        className="h-full w-full"
        imgStyle={{ objectFit: 'contain' }}
      />
    ),
    title: 'Machine Learning & MLOps',
    subtitle: 'Modeling at scale',
    chips: [
      'Scikit-Learn',
      'TensorFlow',
      'PyTorch',
      'MLflow',
      'XGBoost',
      'Airflow',
      'Spark',
      'Docker',
    ],
  },
  {
    icon: (
      <StaticImage
        src="../../../images/v5/CurriculumIcons/DataScience/Icon3.webp"
        alt="GenAI & Agentic AI"
        loading="lazy"
        placeholder="blurred"
        className="h-full w-full"
        imgStyle={{ objectFit: 'contain' }}
      />
    ),
    title: 'GenAI & Agentic AI',
    subtitle: 'The new differentiator',
    featured: true,
    chips: [
      'ChromaDB',
      'Pinecone',
      'LLM APIs',
      'RAG',
      'LangChain',
      'Multimodal',
    ],
  },
];
